/**
 * Mirrors hh-fast-ship-status.js's relocate + variant-change-detection
 * pattern (Sternify's pill picker doesn't dispatch the theme's native
 * variant:update event, so polling the id input after a page-wide click is
 * the reliable way to notice a variant switch). On a detected change,
 * re-fetches this section via the Section Rendering API so the in-stock
 * state, scarcity count, and the Express radio's disabled/checked state are
 * all recomputed server-side from the newly selected variant's real
 * inventory - including which option is pre-selected by default (Express
 * whenever available), so a variant switch doesn't need any client-side
 * logic to decide that; the fresh markup's own `checked` attribute is left
 * as-is after the swap.
 *
 * FIXED 2026-09-26 (stale radio reference): never cache `standardRadio` /
 * `expressRadio` node references - always query the live DOM. A cached
 * reference could point at a node detached by a concurrent fetchAndRender()
 * innerHTML swap, silently reporting the wrong selection state.
 *
 * FIXED 2026-09-26 (cart drawer not refreshing): the synthetic `cart:update`
 * event now matches the theme's real CartUpdateEvent shape (see
 * assets/events.js) and requests `sections` HTML from /cart/add.js itself,
 * exactly like snippets/product-upgrades.liquid already does for its own
 * upgrade add-ons - including reusing its `source: 'product-form-component'`
 * spoof so assets/cart-icon.js increments the bubble count correctly.
 *
 * The Express cart line is tagged with a hidden `_hh_linked_variant`
 * property (the watch variant it was added alongside, read from
 * data-watch-variant-id). Properties starting with `_` are hidden from the
 * customer-facing cart/checkout but still visible on the order in Admin.
 * This link is what assets/hh-express-delivery-guard.js uses to remove an
 * orphaned Express line if that watch is later removed from the cart or
 * swapped for a different one - otherwise nothing in Shopify's cart keeps an
 * add-on tied to the line it was meant to ship alongside.
 *
 * Important: the Express Delivery variant's OWN inventory policy must be
 * "continue selling" in Shopify admin (not "deny"). Availability here is
 * gated entirely by the WATCH's own selected-variant stock (see the liquid
 * side) - the Express variant is just a flat-price service SKU layered on
 * top, so its own inventory count is irrelevant and must never block this
 * add.
 */
class HHExpressDelivery extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.sectionId) {
      this.dataset.sectionId = this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || '';
    }

    if (this.dataset.relocated !== 'true') {
      queueMicrotask(() => this.relocate());
    }

    this.variantId = this.dataset.expressVariantId;

    this._onFormClick = () => {
      const idAtClickTime = this.getIdInput()?.value;
      this.pollForChange(idAtClickTime, 0);
    };
    document.addEventListener('click', this._onFormClick);

    this._onCartUpdate = () => this.handleCartUpdate();
    document.addEventListener('cart:update', this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onFormClick);
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  relocate() {
    if (this.dataset.relocated === 'true' || !this.isConnected) return;
    const target =
      document.querySelector('[data-product-upgrades]') ||
      document.querySelector('.product-form-buttons') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('.st_pi-form');
    if (target && target.parentNode) {
      this.dataset.relocated = 'true';
      target.parentNode.insertBefore(this, target);
    }
  }

  getIdInput() {
    return (
      document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      document.querySelector('.st_pi-form input[name="id"]')
    );
  }

  // Always live-queried - never cached - so a concurrent fetchAndRender()
  // innerHTML swap can't leave this pointing at a detached node.
  getExpressRadio() {
    return this.querySelector('[data-express-radio="express"]');
  }

  getStandardRadio() {
    return this.querySelector('[data-express-radio="standard"]');
  }

  isExpressSelected() {
    const radio = this.getExpressRadio();
    return !!(radio && radio.checked && !radio.disabled);
  }

  pollForChange(previousId, attempts) {
    if (attempts > 20) return;
    setTimeout(() => {
      const currentId = this.getIdInput()?.value;
      if (currentId && currentId !== previousId) {
        this.fetchAndRender(currentId);
      } else {
        this.pollForChange(previousId, attempts + 1);
      }
    }, 100);
  }

  async fetchAndRender(variantId) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    try {
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('hh-express-delivery');
      if (fresh) {
        this.innerHTML = fresh.innerHTML;
        if (fresh.dataset.watchVariantId) {
          this.dataset.watchVariantId = fresh.dataset.watchVariantId;
        }
      }
    } catch (err) {
      console.error('[hh-express-delivery]', err);
    }
  }

  handleCartUpdate() {
    if (!this.isExpressSelected()) return;
    const standardRadio = this.getStandardRadio();
    if (standardRadio) standardRadio.checked = true; // consume the intent so a later unrelated cart:update can't re-trigger

    const sectionIds = Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(this.variantId, 10),
            quantity: 1,
            properties: { _hh_linked_variant: this.dataset.watchVariantId || '' },
          },
        ],
        sections: sectionIds.join(','),
      }),
    })
      .then((r) => {
        if (!r.ok) {
          return r.json().then((data) => {
            throw new Error('Express Delivery add-to-cart failed (' + r.status + '): ' + (data && data.description || JSON.stringify(data)));
          });
        }
        return r.json();
      })
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: String(this.variantId),
              data: {
                source: 'product-form-component',
                itemCount: 1,
                sections: data.sections,
              },
            },
          })
        );
      })
      .catch((err) => {
        console.error('[hh-express-delivery]', err.message || err);
        const statusText = this.querySelector('[data-express-status-text]');
        if (statusText) {
          statusText.textContent = this.dataset.failText || statusText.textContent;
        }
      });
  }
}

if (!customElements.get('hh-express-delivery')) {
  customElements.define('hh-express-delivery', HHExpressDelivery);
}
